import { useEffect } from 'react';
import {
  ArrowDown,
  ArrowUpRight,
  BookOpenCheck,
  CheckCircle2,
  FlaskConical,
  Presentation,
  ShieldCheck,
  Users,
} from 'lucide-react';

const FORM_URL = 'https://form.jotform.com/252570644185056';

const proposalFocus = [
  {
    icon: FlaskConical,
    title: 'Practical by design',
    description: 'Hands-on labs, demonstrations, and scenarios that reflect how security work happens in practice.',
  },
  {
    icon: BookOpenCheck,
    title: 'Clear learning outcomes',
    description: 'A focused curriculum that tells participants exactly what they will be able to do after the training.',
  },
  {
    icon: ShieldCheck,
    title: 'Current and relevant',
    description: 'Techniques, tooling, and defensive guidance grounded in today’s security landscape.',
  },
];

const reviewSteps = [
  'Submit your trainer profile and course outline.',
  'Our review panel evaluates the content, relevance, and practical value.',
  'Shortlisted trainers present their curriculum and labs to our team.',
  'We share the final decision, feedback, and required refinements.',
  'Confirmed trainings are published for registration.',
];

const TrainerProposalPage = () => {
  useEffect(() => {
    const previousTitle = document.title;
    const existingRobots = document.querySelector('meta[name="robots"]');
    const previousRobotsContent = existingRobots?.getAttribute('content');
    const robots = existingRobots || document.createElement('meta');

    document.title = 'Trainer Proposals | Byt3con Academy';
    robots.setAttribute('name', 'robots');
    robots.setAttribute('content', 'noindex, nofollow');

    if (!existingRobots) {
      document.head.appendChild(robots);
    }

    return () => {
      document.title = previousTitle;
      if (existingRobots) {
        if (previousRobotsContent) {
          existingRobots.setAttribute('content', previousRobotsContent);
        } else {
          existingRobots.removeAttribute('content');
        }
      } else {
        robots.remove();
      }
    };
  }, []);

  return (
    <div className="min-h-screen pt-20" data-testid="trainer-proposal-page">
      <section className="relative overflow-hidden py-16 md:py-24" data-testid="trainer-proposal-hero">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="hero-glow left-1/3 top-0" />

        <div className="container-main relative z-10">
          <div className="max-w-4xl">
            <p className="mb-5 font-mono text-sm uppercase tracking-[0.24em] text-[var(--primary)]">
              ./call_for_trainers
            </p>
            <h1 className="mb-6 text-4xl font-bold tracking-tighter text-[var(--text-main)] md:text-6xl lg:text-7xl">
              Teach what you have
              <span className="block text-[var(--primary)]">learned in the field.</span>
            </h1>
            <p className="max-w-3xl text-lg leading-relaxed text-[var(--text-muted)] md:text-xl">
              Byt3con invites experienced cybersecurity practitioners, researchers, and builders to propose
              intensive, hands-on training programs for our community.
            </p>

            <div className="mt-10 flex flex-col gap-4 sm:flex-row">
              <a
                href="#submit-proposal"
                className="inline-flex items-center justify-center gap-2 bg-[var(--primary)] px-7 py-4 font-mono text-sm font-bold uppercase tracking-wider text-black transition-colors hover:bg-[var(--primary-dim)]"
                data-testid="start-proposal-btn"
              >
                Start your proposal <ArrowDown className="h-4 w-4" />
              </a>
              <a
                href={FORM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 border border-[var(--border)] px-7 py-4 font-mono text-sm uppercase tracking-wider text-[var(--text-main)] transition-colors hover:border-[var(--primary)] hover:text-[var(--primary)]"
              >
                Open form separately <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="border-y border-[var(--border)] py-14 md:py-20">
        <div className="container-main">
          <div className="mb-10 max-w-2xl">
            <p className="mb-3 font-mono text-sm uppercase tracking-wider text-[var(--primary)]">What we value</p>
            <h2 className="text-3xl font-bold text-[var(--text-main)] md:text-4xl">Training that transfers real capability</h2>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {proposalFocus.map(({ icon: Icon, title, description }) => (
              <article key={title} className="border border-[var(--border)] bg-[var(--surface)] p-7">
                <div className="mb-5 flex h-12 w-12 items-center justify-center border border-[var(--primary)] bg-[var(--primary)]/10">
                  <Icon className="h-6 w-6 text-[var(--primary)]" />
                </div>
                <h3 className="mb-3 text-xl font-bold text-[var(--text-main)]">{title}</h3>
                <p className="leading-relaxed text-[var(--text-muted)]">{description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="container-main grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-16">
          <div>
            <div className="mb-6 flex h-14 w-14 items-center justify-center border border-[var(--primary)] bg-[var(--primary)]/10">
              <Presentation className="h-7 w-7 text-[var(--primary)]" />
            </div>
            <p className="mb-3 font-mono text-sm uppercase tracking-wider text-[var(--primary)]">Who should apply</p>
            <h2 className="mb-5 text-3xl font-bold text-[var(--text-main)] md:text-4xl">Practitioners with something concrete to teach</h2>
            <p className="text-lg leading-relaxed text-[var(--text-muted)]">
              We welcome proposals from security professionals with demonstrable experience and a strong point of
              view on how their subject should be taught. New trainers are welcome when the material and delivery
              plan are rigorous.
            </p>
            <div className="mt-7 flex items-start gap-3 border-l-2 border-[var(--primary)] bg-[var(--surface)] p-5">
              <Users className="mt-0.5 h-5 w-5 shrink-0 text-[var(--primary)]" />
              <p className="text-sm leading-relaxed text-[var(--text-muted)]">
                Co-trainer proposals are welcome. Include each trainer’s role and relevant experience in the form.
              </p>
            </div>
          </div>

          <div className="border border-[var(--border)] bg-[var(--surface)] p-7 md:p-9">
            <div className="mb-7 flex items-center gap-2 border-b border-[var(--border)] pb-5">
              <span className="h-3 w-3 rounded-full bg-[#ff5f56]" />
              <span className="h-3 w-3 rounded-full bg-[#ffbd2e]" />
              <span className="h-3 w-3 rounded-full bg-[#27ca40]" />
              <span className="ml-3 font-mono text-sm text-[var(--text-muted)]">review_process.sh</span>
            </div>
            <h2 className="mb-7 text-2xl font-bold text-[var(--text-main)]">What happens next</h2>
            <ol className="space-y-6">
              {reviewSteps.map((step, index) => (
                <li key={step} className="grid grid-cols-[2rem_minmax(0,1fr)] items-start gap-4">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center border border-[var(--primary)] font-mono text-sm font-bold text-[var(--primary)]">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <p className="min-h-8 leading-8 text-[var(--text-muted)]">{step}</p>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section id="submit-proposal" className="scroll-mt-24 border-t border-[var(--border)] py-16 md:py-24" data-testid="proposal-form-section">
        <div className="container-main">
          <div className="mx-auto mb-10 max-w-3xl text-center">
            <CheckCircle2 className="mx-auto mb-5 h-10 w-10 text-[var(--primary)]" />
            <p className="mb-3 font-mono text-sm uppercase tracking-wider text-[var(--primary)]">Submit your proposal</p>
            <h2 className="mb-4 text-3xl font-bold text-[var(--text-main)] md:text-5xl">Tell us what you want to teach</h2>
            <p className="text-lg leading-relaxed text-[var(--text-muted)]">
              Complete the form below with your background, proposed topic, audience, and course structure.
            </p>
          </div>

          <div className="mx-auto max-w-5xl overflow-hidden border border-[var(--border)] bg-white">
            <iframe
              title="Byt3con trainer proposal form"
              src={FORM_URL}
              className="block min-h-[1500px] w-full border-0 md:min-h-[1650px]"
              loading="lazy"
              data-testid="trainer-proposal-form"
            />
          </div>

          <p className="mx-auto mt-6 max-w-3xl text-center text-sm text-[var(--text-muted)]">
            If the embedded form does not load,{' '}
            <a href={FORM_URL} target="_blank" rel="noopener noreferrer" className="font-mono text-[var(--primary)] hover:underline">
              open the proposal form in a new tab
            </a>.
          </p>
        </div>
      </section>
    </div>
  );
};

export default TrainerProposalPage;
